import { APP_NAV } from '../context/AppNavigationContext';

export function exportToCsv(filename, rows, columns) {
  if (!rows || rows.length === 0) return;

  const header = columns.map((c) => c.label).join(',');
  const body = rows.map((row) =>
    columns.map((c) => {
      const val = c.value(row);
      if (typeof val === 'string' && (val.includes(',') || val.includes('\n') || val.includes('"'))) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val ?? '';
    }).join(',')
  ).join('\n');

  const csv = `${header}\n${body}`;
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function revenueByDateColumns(currencyCode = 'DKK') {
  return [
    { label: 'Date', value: (r) => r.date },
    { label: `Total Revenue (${currencyCode})`, value: (r) => r.totalRevenue },
    { label: 'Invoice Count', value: (r) => r.invoiceCount },
  ];
}

export function revenueByProductColumns(currencyCode = 'DKK') {
  return [
    { label: 'Product Name', value: (r) => r.productName },
    { label: 'Product Type', value: (r) => r.productType || '' },
    { label: 'Quantity Sold', value: (r) => r.quantity },
    { label: `Total Revenue (${currencyCode})`, value: (r) => r.totalRevenue },
  ];
}

export function revenueSummaryColumns(currencyCode = 'DKK') {
  return [
    { label: 'Metric', value: (r) => r.metric },
    { label: 'Value', value: (r) => r.value },
  ];
}

export function topEventsColumns(currencyCode = 'DKK') {
  return [
    { label: 'Event Name', value: (r) => r.eventName },
    { label: 'Venue Name', value: (r) => r.venueName || '' },
    { label: 'Invoice Count', value: (r) => r.invoiceCount },
    { label: 'Units Sold', value: (r) => r.quantity },
    { label: `Total Revenue (${currencyCode})`, value: (r) => r.totalRevenue },
  ];
}

export function topVenuesColumns(currencyCode = 'DKK') {
  return [
    { label: 'Venue Name', value: (r) => r.venueName },
    { label: 'Invoice Count', value: (r) => r.invoiceCount },
    { label: 'Units Sold', value: (r) => r.quantity },
    { label: `Total Revenue (${currencyCode})`, value: (r) => r.totalRevenue },
  ];
}

export function aovByDateColumns(currencyCode = 'DKK') {
  return [
    { label: 'Date', value: (r) => r.date },
    { label: `Average Order Value (${currencyCode})`, value: (r) => r.averageOrderValue ?? r.aov ?? r.value },
  ];
}

export const REVENUE_BY_DATE_COLUMNS = revenueByDateColumns();
export const REVENUE_BY_PRODUCT_COLUMNS = revenueByProductColumns();
export const REVENUE_SUMMARY_COLUMNS = revenueSummaryColumns();
export const TOP_EVENTS_COLUMNS = topEventsColumns();
export const TOP_VENUES_COLUMNS = topVenuesColumns();

const PAGE_EXPORT_SLUGS = {
  [APP_NAV.OVERVIEW]: 'overview',
  [APP_NAV.TRENDS]: 'trends',
  [APP_NAV.PRODUCTS]: 'products',
  [APP_NAV.EVENTS]: 'events',
  [APP_NAV.VENUES]: 'venues',
};

export function exportPageDashboardData(pageId, data, { topEventRows = [], aovData = [], dateLabel, currencyCode = 'DKK' } = {}) {
  const date = dateLabel || new Date().toISOString().slice(0, 10);
  const slug = PAGE_EXPORT_SLUGS[pageId] || 'dashboard';
  const summaryColumns = revenueSummaryColumns(currencyCode);

  switch (pageId) {
    case APP_NAV.OVERVIEW:
      if (data?.summary) {
        exportToCsv(`vi-${slug}-summary-${date}.csv`, [
          { metric: `Total Revenue (${currencyCode})`, value: data.summary.totalRevenue },
          { metric: 'Invoice Count', value: data.summary.invoiceCount },
          { metric: `Average Order Value (${currencyCode})`, value: data.summary.averageOrderValue },
          { metric: 'Units Sold', value: data.summary.unitsSold },
        ], summaryColumns);
      }
      if (data?.revenueByDate?.length) {
        exportToCsv(`vi-${slug}-revenue-by-date-${date}.csv`, data.revenueByDate, revenueByDateColumns(currencyCode));
      }
      if (data?.revenueByProduct?.length) {
        exportToCsv(`vi-${slug}-revenue-by-product-${date}.csv`, data.revenueByProduct, revenueByProductColumns(currencyCode));
      }
      if (topEventRows?.length) {
        exportToCsv(`vi-${slug}-top-events-${date}.csv`, topEventRows, topEventsColumns(currencyCode));
      }
      if (data?.topVenues?.length) {
        exportToCsv(`vi-${slug}-top-venues-${date}.csv`, data.topVenues, topVenuesColumns(currencyCode));
      }
      break;
    case APP_NAV.TRENDS:
      if (data?.revenueByDate?.length) {
        exportToCsv(`vi-${slug}-revenue-by-date-${date}.csv`, data.revenueByDate, revenueByDateColumns(currencyCode));
      }
      if (aovData?.length) {
        exportToCsv(`vi-${slug}-aov-by-date-${date}.csv`, aovData, aovByDateColumns(currencyCode));
      }
      break;
    case APP_NAV.PRODUCTS:
      if (data?.revenueByProduct?.length) {
        exportToCsv(`vi-${slug}-revenue-by-product-${date}.csv`, data.revenueByProduct, revenueByProductColumns(currencyCode));
      }
      break;
    case APP_NAV.EVENTS:
      if (topEventRows?.length) {
        exportToCsv(`vi-${slug}-top-events-${date}.csv`, topEventRows, topEventsColumns(currencyCode));
      }
      break;
    case APP_NAV.VENUES:
      if (data?.topVenues?.length) {
        exportToCsv(`vi-${slug}-top-venues-${date}.csv`, data.topVenues, topVenuesColumns(currencyCode));
      }
      break;
    default:
      break;
  }
}

export function exportAllDashboardData(data, topEventRows, dateLabel) {
  exportPageDashboardData(APP_NAV.OVERVIEW, data, { topEventRows, dateLabel });
}
